const BARE_SCHEME = 'sama-coachee://'

// Maps the SPA's pathname to the app's deep-link path. The dynamic
// `/coachee/auth/sso/:companyId/:ssoId` route is handled separately in the
// builder below because the app path is composed from useParams.
// Matches the company-SSO route's pathname literally (two non-empty path
// segments after /coachee/auth/sso/). Used to gate the dynamic-route branch
// of the builder so stale routeParams from a different route can't be used
// to compose a deep-link for a page the user isn't on.
const COMPANY_SSO_PATHNAME_PATTERN = /^\/coachee\/auth\/sso\/[^/]+\/[^/]+$/

const deepLinkPathMap: Record<string, string> = {
    '/coachee/login': 'magic-login',
    '/coachee/verify-password': 'verify-password',
    '/coachee/auth/okta/sso': 'auth/okta/sso',
    '/coachee/auth/sama/sso': 'auth/sama/sso',
    '/coachee/auth/sso/microsoft': 'auth/sso/microsoft',
}

// Param-list derived from the backend's getLoginUrl in
// api/src/api/service/authentication/operations.authentication.service.ts.
// `state`/`authority_url`/`client_id` are MS-SSO-only; OAuth state validation
// depends on `state` surviving the bounce, and `authority_url` carries the
// full Microsoft authority (SAM-403) so the mobile apps can configure native
// MSAL for custom (per-company) authorities. `redirect_uri` is deliberately
// excluded — it's a coachee-web-internal value injected on the post-do_login
// re-entry, not part of email-link traffic.
const ALLOWED_PARAMS = [
    'details',
    'phoneNumber',
    'magicToken',
    'state',
    'authority_url',
    'client_id',
    'lng',
] as const

export interface DeepLinkRouteParams {
    companyId?: string
    ssoId?: string
}

/**
 * Build the `sama-coachee://...` deep-link URL for the "Open in Sama app"
 * action, based on the current SPA route and inbound search params.
 *
 * Returns the bare `sama-coachee://` scheme (cold-open the app) when:
 * - The pathname is unmapped (defensive — catch-all redirect should catch this).
 * - No whitelisted auth-context params are present.
 *
 * @param pathname - The SPA pathname (`window.location.pathname`).
 * @param searchParams - Inbound query params (from `useSearchParams`).
 * @param routeParams - Dynamic route params (from `useParams`). Only consumed
 *   for the company-specific Microsoft SSO route.
 */
export const buildSamaCoacheeDeepLink = (
    pathname: string,
    searchParams: URLSearchParams,
    routeParams: DeepLinkRouteParams = {},
): string => {
    // Resolve the app path: static map first, then dynamic company-SSO match.
    // The dynamic branch only fires when the pathname looks like a company-SSO
    // route — otherwise stale routeParams from a previous route could build a
    // deep-link for a page the user isn't actually on.
    let appPath: string | undefined = deepLinkPathMap[pathname]
    if (
        !appPath &&
        COMPANY_SSO_PATHNAME_PATTERN.test(pathname) &&
        routeParams.companyId &&
        routeParams.ssoId
    ) {
        // Trim + validate non-empty after trimming, so " " or "\t" doesn't
        // compose a path with empty segments. encodeURIComponent is the right
        // primitive for path segments — DOMPurify is an HTML sanitiser and
        // would be a no-op on plain URL strings, suggesting protection that
        // doesn't exist.
        const companyId = routeParams.companyId.trim()
        const ssoId = routeParams.ssoId.trim()
        if (companyId && ssoId) {
            appPath = `auth/sso/${encodeURIComponent(companyId)}/${encodeURIComponent(ssoId)}`
        }
    }

    if (!appPath) {
        return BARE_SCHEME
    }

    // Collect whitelisted params present in the inbound URL.
    const params = new URLSearchParams()
    let hasAuthContext = false
    for (const key of ALLOWED_PARAMS) {
        const value = searchParams.get(key)
        if (value !== null) {
            params.set(key, value)
            hasAuthContext = true
        }
    }

    if (!hasAuthContext) {
        // Cold-open: route is registered but no auth context to carry.
        return BARE_SCHEME
    }

    // Add do_login=true after the inbound params so the app knows this is a
    // post-chooser arrival, matching the convention used for the web bounce.
    params.set('do_login', 'true')

    return `${BARE_SCHEME}${appPath}?${params.toString()}`
}
